/**
 * Client for the new profile-store endpoints: assistants + profile +
 * conversations + single-conversation history.
 */
import { acquireToken, USE_REAL_AUTH } from "../auth/msal";
import type { ToolRoundTrip } from "../types/chat";

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

/**
 * Public-safe assistant shape, as returned from `/api/assistants` and
 * `/api/assistants/visible`. Mirrors `AssistantPublicOut` on the
 * backend — no `system_prompt` / `acl_scope` / `chat_deployment` leaks
 * to non-admin users. Admin pages keep using `admin.ts::AssistantRow`
 * for the full shape.
 */
export interface Assistant {
  slug: string;
  name: string;
  description: string;
  settings: Record<string, unknown>;
  enabled: boolean;
  /** Put first on the Hub: made for one of your teams, for nobody in
   * particular, or used recently (ADR 0063). */
  for_you?: boolean;
}

export interface ConversationSummary {
  id: number;
  assistant_slug: string;
  title: string;
  created_at: string;
  updated_at: string;
  /** ADR 0019 — selected personal-KB ids for this conversation. */
  kb_ids?: number[];
}

export interface StoredMessage {
  id: number;
  conversation_id: number;
  sequence: number;
  role: "user" | "assistant" | "system";
  content: string;
  retrieval: {
    filter?: string;
    groups?: string[];
    hits?: Array<{ n: number; id: string; title: string; url: string }>;
  } | null;
  /** Persisted tool-loop frames (run_sql charts, search hits) so they
   *  rehydrate on reload. Null for turns that used no tools. */
  tools?: ToolRoundTrip[] | null;
  created_at: string;
}

export interface FullConversation {
  conversation: ConversationSummary;
  messages: StoredMessage[];
}

/** Shape returned by `GET /api/chunks/{id}/context?width=N` — backs
 * the citation-preview pane (ADR 0018 Phase D). All `before` chunks
 * come before `centre` in document order; `after` after. Each chunk
 * carries the same fields the citation popup shows. */
export interface ChunkContextChunk {
  id: string;
  title: string;
  url: string;
  source: string;
  section_idx: number;
  section_title: string;
  content: string;
  chunk_summary: string;
  topics: string[];
  cites: string[];
}

export interface ChunkContext {
  parent_id: string;
  centre_idx: number;
  doc_title: string;
  doc_url: string;
  doc_source: string;
  centre: ChunkContextChunk | null;
  before: ChunkContextChunk[];
  after: ChunkContextChunk[];
}

async function authHeaders(devUser?: string): Promise<Record<string, string>> {
  if (USE_REAL_AUTH) {
    const t = await acquireToken();
    return t ? { Authorization: `Bearer ${t.token}` } : {};
  }
  return devUser ? { "X-Dev-User": devUser } : {};
}

/** Shape returned by `GET /api/profile`. Includes the resolved group
 * set so the SPA can gate optional nav affordances (Contracts dashboard
 * link, etc.) without a separate call. The server is still the
 * authoritative gate — `groups` here is for UX clarity. */
export interface MyProfile {
  oid: string;
  email: string | null;
  display_name: string | null;
  groups: string[];
  is_admin: boolean;
  /** ADR 0072 — admin-area permissions: `admin.read` alone is the Viewer
   * role (read-only). Absent on older backends. */
  permissions?: string[];
  preference: {
    default_assistant_slug: string | null;
    ui: Record<string, unknown>;
  };
  /** AI001 §4/§5 — the policy-acknowledgement gate. Show the gate when
   * `version` is non-empty and differs from `acknowledged`; re-issuing the
   * policy under a new version re-arms it for everyone. */
  ai_policy?: {
    version: string;
    url: string;
    acknowledged: string;
  };
  /** EU AI Act Art. 4 — onboarding videos. When `gated`, an assistant opens
   * only once every video applying to it is in `completed` at its current
   * version. /api/chat enforces the same rule (428). */
  onboarding?: {
    mode: "off" | "pilot" | "on";
    gated: boolean;
    videos: OnboardingVideo[];
    /** video id → the version this person completed */
    completed: Record<string, string>;
  };
  /** Whole features the deployment has switched on or off. */
  features?: { kb_sharing: boolean };
}

export interface OnboardingVideo {
  id: string;
  version: string;
  title: string;
  duration_s: number;
  /** "*" = every assistant, else the slugs it applies to */
  applies_to: "*" | string[];
  has_captions: boolean;
}

/** The first video this person must still watch before using `slug`. */
export function pendingOnboarding(
  profile: MyProfile | null | undefined,
  slug: string,
): OnboardingVideo | null {
  const ob = profile?.onboarding;
  if (!ob?.gated || !slug) return null;
  return (
    ob.videos.find(
      (v) =>
        (v.applies_to === "*" || v.applies_to.includes(slug)) &&
        ob.completed[v.id] !== v.version,
    ) ?? null
  );
}

/**
 * Start an empty conversation so a file can be attached before the first
 * question. The composer used to refuse an upload until a message existed
 * ("Send a first message so I can attach this file…"), which is backwards:
 * attaching the document you want to ask about is the natural first move.
 */
export async function createConversation(
  assistantSlug: string,
  devUser?: string,
  title = "Untitled",
): Promise<ConversationSummary | null> {
  const r = await fetch(`${API_BASE}/api/conversations`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(await authHeaders(devUser)),
    },
    body: JSON.stringify({ assistant_slug: assistantSlug, title }),
  });
  if (!r.ok) return null;
  return (await r.json()) as ConversationSummary;
}

/** What the conversation upload route answers (the fields callers use). */
export interface ConversationUpload {
  filename?: string;
  count?: number;
  index_status?: string;
  index_error?: string | null;
}

/** Attach one file to a conversation. Throws with the server's reason. */
export async function uploadConversationFile(
  conversationId: number,
  file: File,
  devUser?: string,
): Promise<ConversationUpload> {
  const form = new FormData();
  form.append("file", file);
  const r = await fetch(`${API_BASE}/api/conversations/${conversationId}/uploads`, {
    method: "POST",
    headers: await authHeaders(devUser),
    body: form,
  });
  if (!r.ok) {
    let detail = "";
    try {
      const body = await r.json();
      detail = typeof body?.detail === "string" ? body.detail : "";
    } catch {
      /* not JSON */
    }
    throw new Error(detail || `upload failed (${r.status})`);
  }
  return (await r.json()) as ConversationUpload;
}

export async function getMyProfile(
  devUser?: string,
): Promise<MyProfile | null> {
  const r = await fetch(`${API_BASE}/api/profile`, {
    cache: "no-store",
    headers: await authHeaders(devUser),
  });
  if (!r.ok) return null;
  return (await r.json()) as MyProfile;
}

/** Task #294 — submit or update the caller's vote on an assistant
 * message. Idempotent at the row level — re-voting upserts the same
 * (message, user) row, the backend handles it. Returns the persisted
 * state so the caller can reflect updated_at without an extra GET. */
export async function setMessageFeedback(
  conversationId: number,
  messageId: number,
  payload: { kind: "up" | "down"; comment?: string | null },
  devUser?: string,
): Promise<{
  kind: "up" | "down";
  comment: string | null;
  updated_at: string;
} | null> {
  const r = await fetch(
    `${API_BASE}/api/conversations/${conversationId}/messages/${messageId}/feedback`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(await authHeaders(devUser)),
      },
      body: JSON.stringify(payload),
    },
  );
  if (!r.ok) return null;
  return (await r.json()) as {
    kind: "up" | "down";
    comment: string | null;
    updated_at: string;
  };
}

export async function clearMessageFeedback(
  conversationId: number,
  messageId: number,
  devUser?: string,
): Promise<boolean> {
  const r = await fetch(
    `${API_BASE}/api/conversations/${conversationId}/messages/${messageId}/feedback`,
    {
      method: "DELETE",
      headers: await authHeaders(devUser),
    },
  );
  return r.ok;
}

/** ADR 0044 — download one assistant message as HTML or DOCX. */
export async function downloadMessageExport(
  conversationId: number,
  messageId: number,
  format: "html" | "docx",
  devUser?: string,
): Promise<boolean> {
  const r = await fetch(
    `${API_BASE}/api/conversations/${conversationId}/messages/${messageId}/export?format=${format}`,
    { headers: await authHeaders(devUser) },
  );
  if (!r.ok) return false;
  const blob = await r.blob();
  const cd = r.headers.get("Content-Disposition") || "";
  const match = /filename="([^"]+)"/i.exec(cd);
  const filename = match?.[1] || `choir-export.${format}`;
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
  return true;
}

export async function listAssistants(devUser?: string): Promise<Assistant[]> {
  // `/api/assistants` is auth-gated; returns the public-safe AssistantPublicOut
  // shape (no system_prompt / acl_scope leaks). The Bearer token rides on
  // this call so REQUIRE_AUTH=true doesn't 401 it. Mirrors
  // listVisibleAssistants() — prefer that for picker / hub surfaces.
  const r = await fetch(`${API_BASE}/api/assistants`, {
    headers: await authHeaders(devUser),
    cache: "no-store",
  });
  if (!r.ok) return [];
  return (await r.json()) as Assistant[];
}

/** Same shape as `listAssistants` but filtered by the caller's AD
 * groups against each assistant's `acl_scope`. Backs the DOIT AI
 * Hub landing page — users see only the assistants they're allowed
 * into. Falls back to `listAssistants()` shape on dev paths that
 * don't carry a user identity. */
export async function listVisibleAssistants(
  devUser?: string,
): Promise<Assistant[]> {
  const r = await fetch(`${API_BASE}/api/assistants/visible`, {
    headers: await authHeaders(devUser),
    cache: "no-store",
  });
  if (!r.ok) return [];
  return (await r.json()) as Assistant[];
}

// ── DOIT AI Board: assistant-request queue ──────────────────────────────

export interface AiRequestIn {
  proposed_slug?: string;
  proposed_name?: string;
  use_case: string;
  data_sources?: string;
  target_audience?: string;
  business_case?: string;
}

export interface AiRequestOut {
  id: number;
  requester_oid: string;
  requester_email: string;
  proposed_slug: string;
  proposed_name: string;
  use_case: string;
  data_sources: string;
  target_audience: string;
  business_case: string;
  status: string;
  notes: string;
  decided_by: string | null;
  decided_at: string | null;
  created_at: string;
  updated_at: string;
}

export class AiRequestError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "AiRequestError";
  }
}

export async function submitAiRequest(
  body: AiRequestIn,
  devUser?: string,
): Promise<AiRequestOut> {
  const r = await fetch(`${API_BASE}/api/ai-requests`, {
    method: "POST",
    headers: {
      ...(await authHeaders(devUser)),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const text = await r.text();
    throw new AiRequestError(r.status, text || `submit failed (${r.status})`);
  }
  return (await r.json()) as AiRequestOut;
}

export async function listConversations(
  devUser?: string,
  assistantSlug?: string,
): Promise<ConversationSummary[]> {
  const qs = assistantSlug
    ? `?assistant_slug=${encodeURIComponent(assistantSlug)}`
    : "";
  const r = await fetch(`${API_BASE}/api/conversations${qs}`, {
    headers: await authHeaders(devUser),
    cache: "no-store",
  });
  if (!r.ok) return [];
  return (await r.json()) as ConversationSummary[];
}

export async function getConversation(
  id: number,
  devUser?: string,
): Promise<FullConversation | null> {
  const r = await fetch(`${API_BASE}/api/conversations/${id}`, {
    headers: await authHeaders(devUser),
    cache: "no-store",
  });
  if (!r.ok) return null;
  return (await r.json()) as FullConversation;
}

export async function deleteConversation(
  id: number,
  devUser?: string,
): Promise<boolean> {
  const r = await fetch(`${API_BASE}/api/conversations/${id}`, {
    method: "DELETE",
    headers: await authHeaders(devUser),
  });
  return r.ok;
}

export async function getChunkContext(
  chunkId: string,
  width = 2,
  devUser?: string,
  /** The assistant whose answer cited the chunk. Someone admitted to that
   * assistant by name (ADR 0054) reads the preview under that admittance —
   * they have no Entra group of their own to show for its corpus. */
  assistantSlug?: string,
): Promise<ChunkContext | null> {
  const q = assistantSlug
    ? `&assistant=${encodeURIComponent(assistantSlug)}`
    : "";
  const r = await fetch(
    `${API_BASE}/api/chunks/${encodeURIComponent(chunkId)}/context?width=${width}${q}`,
    { headers: await authHeaders(devUser), cache: "no-store" },
  );
  if (!r.ok) return null;
  return (await r.json()) as ChunkContext;
}

export async function startConversation(
  assistantSlug: string,
  devUser?: string,
  title = "Untitled",
): Promise<ConversationSummary | null> {
  const r = await fetch(`${API_BASE}/api/conversations`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(await authHeaders(devUser)),
    },
    body: JSON.stringify({ assistant_slug: assistantSlug, title }),
  });
  if (!r.ok) return null;
  return (await r.json()) as ConversationSummary;
}

/** Download a document an assistant produced with `create_document`.
 *
 * Separate from `downloadMessageExport` because the two are different acts:
 * that one exports a message the user is already reading, this one fetches a
 * document the assistant built. The variant is decided HERE rather than when
 * the document was created — the backend stores a spec and renders on
 * request, so `anonymise` is a query parameter, not a second document.
 *
 * Returns an error string on failure rather than a bare false: a download
 * that silently does nothing is indistinguishable from a browser blocking
 * it, and the user needs to know which.
 */
export async function downloadGeneratedDocument(
  conversationId: number,
  documentId: string,
  opts: { format?: string; anonymise?: boolean; devUser?: string } = {},
): Promise<string | null> {
  const params = new URLSearchParams();
  if (opts.format) params.set("format", opts.format);
  if (opts.anonymise) params.set("anonymise", "true");
  const qs = params.toString();

  let r: Response;
  try {
    r = await fetch(
      `${API_BASE}/api/conversations/${conversationId}/documents/${encodeURIComponent(documentId)}${qs ? `?${qs}` : ""}`,
      { headers: await authHeaders(opts.devUser) },
    );
  } catch {
    return "Could not reach the server.";
  }
  if (!r.ok) {
    if (r.status === 404) return "This document is no longer available.";
    return `Could not build the document (${r.status}).`;
  }

  const blob = await r.blob();
  const cd = r.headers.get("Content-Disposition") || "";
  const match = /filename="([^"]+)"/i.exec(cd);
  const filename = match?.[1] || "dokument";
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
  return null;
}

// ── AI001 §7 — special-category identifiers in a draft prompt ─────────────

export interface PromptCheck {
  flagged: boolean;
  kinds: string[];
  message: string;
}

/**
 * Ask the backend whether a draft prompt looks like it carries
 * special-category identifiers (a Danish CPR number today).
 *
 * Detection is server-side on purpose: one implementation to reason about,
 * and one place that counts detections for the quarterly governance review.
 * A regex in the browser would report nothing and drift from the backend's.
 *
 * Fails **open**. This is a warning, not an access control — if the check
 * is unreachable the user's message must still go through, because the
 * alternative is a chat tool that stops working when a side endpoint does.
 */
export async function checkPrompt(
  text: string,
  devUser?: string,
): Promise<PromptCheck> {
  try {
    const res = await fetch(`${API_BASE}/api/prompt-check`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(await authHeaders(devUser)),
      },
      body: JSON.stringify({ text }),
    });
    if (!res.ok) return { flagged: false, kinds: [], message: "" };
    return (await res.json()) as PromptCheck;
  } catch {
    return { flagged: false, kinds: [], message: "" };
  }
}

/**
 * Record that the user has confirmed reading the AI policy and user guide.
 *
 * The acknowledgement is written to the audit ledger as well as to their
 * preferences: the preference stops the gate reappearing, the ledger is the
 * evidence Compliance reviews. Returns the acknowledged version, or "" when
 * the gate is disabled for this deployment.
 */
export async function acknowledgePolicy(devUser?: string): Promise<string> {
  const r = await fetch(`${API_BASE}/api/me/acknowledge-policy`, {
    method: "POST",
    headers: await authHeaders(devUser),
  });
  if (!r.ok) return "";
  const body = (await r.json()) as { acknowledged: string };
  return body.acknowledged ?? "";
}


/** Playback began — starts the server-side clock a completion is checked against. */
export async function startOnboarding(videoId: string, devUser?: string): Promise<void> {
  await fetch(`${API_BASE}/api/onboarding/${encodeURIComponent(videoId)}/start`, {
    method: "POST",
    headers: await authHeaders(devUser),
  });
}

/** Record the completion. Returns "" when accepted, else the server's reason
 * (it refuses a completion sooner than the video could have been watched). */
export async function completeOnboarding(
  videoId: string,
  watchedS: number,
  devUser?: string,
): Promise<string> {
  const r = await fetch(`${API_BASE}/api/onboarding/${encodeURIComponent(videoId)}/complete`, {
    method: "POST",
    headers: { ...(await authHeaders(devUser)), "Content-Type": "application/json" },
    body: JSON.stringify({ watched_s: Math.floor(watchedS) }),
  });
  if (r.ok) return "";
  try {
    return String(((await r.json()) as { detail?: unknown }).detail ?? r.statusText);
  } catch {
    return r.statusText;
  }
}

/**
 * Fetch an onboarding asset (the video or its captions) with the caller's
 * token and hand back an object URL — a <video> element cannot send an
 * Authorization header itself. `onProgress` gets 0..1 while downloading.
 */
export async function fetchOnboardingAsset(
  videoId: string,
  kind: "media" | "captions",
  devUser?: string,
  onProgress?: (share: number) => void,
): Promise<string> {
  const r = await fetch(`${API_BASE}/api/onboarding/${encodeURIComponent(videoId)}/${kind}`, {
    headers: await authHeaders(devUser),
  });
  if (!r.ok || !r.body) throw new Error(`${kind} unavailable (${r.status})`);
  const total = Number(r.headers.get("Content-Length") || 0);
  const reader = r.body.getReader();
  const chunks: BlobPart[] = [];
  let got = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    got += value.byteLength;
    if (total && onProgress) onProgress(got / total);
  }
  const type = kind === "media" ? "video/mp4" : "text/vtt";
  return URL.createObjectURL(new Blob(chunks, { type }));
}
